/**
 * Reports history — design handoff: screen-report-history.jsx
 *
 * Three tabs:
 *   1. Sent history — contract × month matrix grid
 *   2. Scheduled — upcoming generation jobs (placeholder for v1)
 *   3. Cadence settings — per-contract cadence cards (placeholder for v1)
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import type { ReportListItem } from '@beacon/core';

import {
  ChevronRight,
  Download,
  ExternalLink,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { ContractLogo } from '@/components/beacon/contract-logo.js';
import { contractDisplay } from '@/lib/contract-display.js';
import { useReports } from '@/hooks/use-reports.js';
import { cn } from '@/lib/cn.js';

type TabId = 'history' | 'scheduled' | 'cadence';

export function ReportsHistory() {
  const [tab, setTab] = useState<TabId>('history');

  const tabs: Array<{ id: TabId; label: string; count?: number }> = [
    { id: 'history', label: 'Sent history' },
    { id: 'scheduled', label: 'Scheduled' },
    { id: 'cadence', label: 'Cadence settings' },
  ];

  return (
    <div className="h-full min-h-0 overflow-auto scrollbar-thin">
      {/* Header */}
      <div className="px-6 pt-5 pb-0 border-b border-ink-600 bg-ink-850">
        <div className="flex items-center gap-2 text-[12px] text-ink-300 mb-1">
          <Link to="/reports" className="hover:text-ink-100">Reports</Link>
          <ChevronRight size={12} />
          <span className="text-ink-200">History</span>
        </div>
        <div className="flex items-end gap-4">
          <div>
            <h1 className="text-[18px] font-semibold leading-tight">Report history</h1>
            <div className="text-[12.5px] text-ink-300 mt-0.5">
              Generation schedule · sent record · per-contract cadence
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="sm">
              <Download size={13} />Export CSV
            </Button>
          </div>
        </div>
        {/* Tabs */}
        <div className="mt-3 flex gap-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                'inline-flex items-center gap-1.5 h-9 px-3 text-[12.5px] font-medium border-b-2 -mb-px transition-colors',
                tab === t.id
                  ? 'border-beacon-400 text-ink-100'
                  : 'border-transparent text-ink-300 hover:text-ink-100'
              )}
            >
              {t.label}
              {t.count != null && (
                <span className="mono text-[10.5px] bg-ink-700 text-ink-300 px-1 rounded">{t.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {tab === 'history' && <SentHistoryTab />}
      {tab === 'scheduled' && <ScheduledTab />}
      {tab === 'cadence' && <CadenceTab />}
    </div>
  );
}

/* ── Sent History — contract × month matrix ── */

function SentHistoryTab() {
  const [search, setSearch] = useState('');
  const reports = useReports(['sent', 'approved', 'draft', 'review']);
  const allRows = reports.data ?? [];

  // Get unique months (last 8) and unique contracts
  const { months, contracts, cellMap } = useMemo(() => {
    const monthSet = new Set<string>();
    const contractMap = new Map<string, { id: string; name: string; type: string }>();
    const cells = new Map<string, ReportListItem>();

    for (const r of allRows) {
      const period = r.periodStartDate.slice(0, 7);
      monthSet.add(period);
      if (!contractMap.has(r.contractId)) {
        contractMap.set(r.contractId, {
          id: r.contractId,
          name: r.contractName,
          type: r.contractType ?? '',
        });
      }
      cells.set(`${r.contractId}:${period}`, r);
    }

    const months = [...monthSet].sort((a, b) => b.localeCompare(a)).slice(0, 8);
    let contracts = [...contractMap.values()];

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      contracts = contracts.filter((c) => c.name.toLowerCase().includes(q));
    }

    return { months, contracts, cellMap: cells };
  }, [allRows, search]);

  if (reports.isLoading) return <HistorySkeleton />;

  if (contracts.length === 0) {
    return (
      <div className="px-6 py-16 text-center">
        <div className="text-[14px] text-ink-100">No reports found</div>
        <div className="text-[12px] text-ink-300 mt-1">
          {search ? 'Try a different search term.' : 'Reports will appear here after generation.'}
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-5">
      {/* Search + filters */}
      <div className="flex items-center gap-2 mb-4 text-[12px]">
        <Input
          placeholder="Search reports…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="!h-8 max-w-[280px]"
        />
        <span className="ml-auto text-[11.5px] text-ink-400 mono">
          {allRows.length} reports · {contracts.length} contracts
        </span>
      </div>

      {/* Matrix grid */}
      <div className="rounded-lg border border-ink-600 overflow-hidden bg-ink-850">
        <div
          className="grid"
          style={{ gridTemplateColumns: `200px repeat(${months.length}, 1fr)` }}
        >
          {/* Header row */}
          <div className="bg-ink-800 border-b border-r border-ink-600 px-3 h-9 flex items-center text-[10.5px] uppercase tracking-wider text-ink-300 font-semibold">
            Contract
          </div>
          {months.map((m) => (
            <div key={m} className="bg-ink-800 border-b border-r border-ink-600 px-2 h-9 flex items-center justify-center text-[11px] text-ink-300 mono">
              {formatMonthShort(m)}
            </div>
          ))}

          {/* Contract rows */}
          {contracts.map((c) => {
            const display = contractDisplay({ id: c.id, name: c.name } as import('@beacon/core').Contract);
            return (
              <div key={c.id} className="contents">
                <div className="border-b border-r border-ink-600 px-3 h-[68px] flex items-center gap-2">
                  <ContractLogo contract={display} size={22} />
                  <div className="text-[12.5px] text-ink-100 truncate">{c.name}</div>
                </div>
                {months.map((m) => {
                  const r = cellMap.get(`${c.id}:${m}`);
                  if (!r) {
                    return (
                      <div key={m} className="border-b border-r border-ink-600 h-[68px] p-2 flex items-center justify-center">
                        <span className="text-[11px] text-ink-500">—</span>
                      </div>
                    );
                  }
                  if (r.status === 'draft' || r.status === 'review') {
                    return (
                      <div key={m} className="border-b border-r border-ink-600 h-[68px] p-1.5 bg-warn-500/[0.05]">
                        <Link
                          to={`/reports/${encodeURIComponent(r.id)}`}
                          className="h-full rounded-md border border-dashed border-warn-500/40 flex flex-col items-center justify-center hover:bg-warn-500/10 transition-colors"
                        >
                          <span className="text-[10.5px] uppercase tracking-wider text-warn-400 font-semibold">
                            {r.status === 'review' ? 'review' : 'draft'}
                          </span>
                          <span className="text-[10px] text-ink-400 mono mt-0.5">in queue</span>
                        </Link>
                      </div>
                    );
                  }
                  return (
                    <Link
                      key={m}
                      to={`/reports/${encodeURIComponent(r.id)}`}
                      className="border-b border-r border-ink-600 h-[68px] p-2 text-left hover:bg-ink-800/60 group block"
                    >
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-ok-500" />
                        <span className="text-[10.5px] text-ink-300 mono">
                          {r.sentAt ? shortDay(r.sentAt) : 'Approved'}
                        </span>
                      </div>
                      <div className="mono text-[14px] leading-none text-ink-100">
                        {r.status === 'sent' ? 'Sent' : 'Approved'}
                      </div>
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-3 flex items-center gap-4 text-[11px] text-ink-400 mono">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-ok-500" />Sent
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-warn-500" />Draft in review queue
        </span>
      </div>
    </div>
  );
}

/* ── Scheduled tab (placeholder) ── */

function ScheduledTab() {
  return (
    <div className="px-6 py-5 max-w-[920px]">
      <div className="rounded-lg border border-info-500/30 bg-info-500/[0.06] p-4 flex items-start gap-3 mb-5">
        <div className="text-[12.5px] flex-1">
          <div className="text-ink-100 font-medium">How generation works</div>
          <div className="text-ink-300 mt-0.5">
            On each contract's cadence date, Beacon runs a generation job that creates the report draft,
            opens a system review ticket assigned to the contract's reviewer, and pings them in Slack.
            Draft tickets appear in the review queue at 06:00 AEST.
          </div>
        </div>
      </div>
      <div className="text-[13px] text-ink-300">
        Scheduled generation jobs will appear here once the cron scheduler is configured.
      </div>
    </div>
  );
}

/* ── Cadence settings tab (placeholder) ── */

function CadenceTab() {
  return (
    <div className="px-6 py-5 max-w-[920px]">
      <div className="text-[12.5px] text-ink-300 mb-4">
        Cadence is configured per contract during onboarding. Edit on the contract detail screen → Settings.
      </div>
      <div className="text-[13px] text-ink-300">
        Per-contract cadence cards will appear here once cadence configuration is built.
      </div>
    </div>
  );
}

/* ── Skeleton ── */

function HistorySkeleton() {
  return (
    <div className="px-6 py-5 space-y-4" aria-busy="true">
      <div className="flex items-center gap-2">
        <Skeleton className="h-8 w-[280px] rounded-md" />
        <Skeleton className="h-3 w-24 ml-auto" />
      </div>
      <div className="rounded-lg border border-ink-600 bg-ink-850 overflow-hidden">
        <div className="grid" style={{ gridTemplateColumns: '200px repeat(6, 1fr)' }}>
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="bg-ink-800 border-b border-r border-ink-600 px-3 h-9 flex items-center">
              <Skeleton className="h-3 w-16" />
            </div>
          ))}
          {Array.from({ length: 3 }).map((_, row) => (
            <div key={row} className="contents">
              <div className="border-b border-r border-ink-600 px-3 h-[68px] flex items-center gap-2">
                <Skeleton className="w-5 h-5 rounded-[4px]" inline />
                <Skeleton className="h-3 w-24" />
              </div>
              {Array.from({ length: 6 }).map((_, col) => (
                <div key={col} className="border-b border-r border-ink-600 h-[68px] p-2">
                  <Skeleton className="h-3 w-12 mb-1" />
                  <Skeleton className="h-4 w-10" />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Helpers ── */

function formatMonthShort(yyyymm: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(yyyymm);
  if (!m) return yyyymm;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m[2]) - 1] ?? m[2]} ${m[1]}`;
}

function shortDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
  });
}
